import React, { FunctionComponent, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SvgIcon from '@mui/material/SvgIcon';

import { ReactComponent as ExecutivesIcon } from '../../static/sustainability_image/executives_icon.svg';
import { ReactComponent as BoardMembersIcon } from '../../static/sustainability_image/boardMembers_icon.svg';
import { ReactComponent as EthicalIcon } from '../../static/sustainability_image/ethical_icon.svg';

import MonthlyStateTable from './MonthlyStateTable';
import DividendTable from './DividendPolicyTable';
import AnalystCoverageTable from './AnalystCoverageTable';
import MajorShareholdersTable from './MajorShareholderTable';
import { useResponsive } from '../../contexts/ResponsiveContext';
import type { AboutInvestorRelationsOverviewContent } from '../../graphql/pimcore/modules/siteContent/types/AboutInvestorRelationsContent';
import StakeholderCommunicationTable from './StakeholderCommunicationTable';
import TaxReportingForms from './TaxReportingForms';

interface OverviewTabProps {
    cmsContent: AboutInvestorRelationsOverviewContent;
}

export interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const cellSx = {
    fontWeight: 500,
    fontSize: '12px',
    textAlign: 'center',
    fontFamily: 'Poppins',
};

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`vertical-tabpanel-${index}`}
            aria-labelledby={`vertical-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ marginTop: '0.42rem', width: '100%' }}>
                    <Typography component="div">{children}</Typography>
                </Box>
            )}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `vertical-tab-${index}`,
        'aria-controls': `vertical-tabpanel-${index}`,
    };
}

interface Panel {
    label: string;
    icon: React.ElementType;
    content: React.ReactNode;
}

const CustomIcon: FunctionComponent<{ component: React.ElementType; expanded: boolean }> = ({
    component: Component,
    expanded,
}) => (
    <SvgIcon
        component={Component}
        inheritViewBox
        sx={{
            marginRight: '0.5rem',
            width: '24px',
            height: '24px',
            path: { fill: expanded ? 'white' : '#E56731' },
        }}
    />
);

const OverviewTab: FunctionComponent<OverviewTabProps> = ({ cmsContent }) => {
    const [value, setValue] = useState(0);
    const { isScreenXxl, isScreenXl, isScreenLg, isScreenMd, isScreenSm, isScreenXs } =
        useResponsive();
    const [expanded, setExpanded] = useState<number | false>(false);

    const handleAccordionChange =
        (panel: number) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
            setExpanded(isExpanded ? panel : false);
        };

    const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };

    const panels: Panel[] = useMemo(
        () => [
            {
                label: cmsContent.MonthlyStatements.title.content,
                icon: ExecutivesIcon,
                content: <MonthlyStateTable cmsContent={cmsContent.MonthlyStatements} />,
            },
            {
                label: cmsContent.StakeholderCommunication.title.content,
                icon: EthicalIcon,
                content: (
                    <StakeholderCommunicationTable
                        cmsContent={cmsContent.StakeholderCommunication}
                    />
                ),
            },
            {
                label: cmsContent.DividendPolicy.title.content,
                icon: BoardMembersIcon,
                content: <DividendTable cmsContent={cmsContent.DividendPolicy} />,
            },
            {
                label: cmsContent.AnalystCoverage.title.content,
                icon: EthicalIcon,
                content: <AnalystCoverageTable cmsContent={cmsContent.AnalystCoverage} />,
            },
            {
                label: cmsContent.ShareholderStructure.title.content,
                icon: EthicalIcon,
                content: (
                    <>
                        <Box marginTop="1rem">
                            <img
                                src={cmsContent.ShareholderStructure.image.imageUrl}
                                alt={cmsContent.ShareholderStructure.title.content}
                                style={{ width: '100%', height: 'auto' }}
                            />
                        </Box>
                        {/*<Box sx={{ ...cellSx, textAlign: 'right' }}>*/}
                        {/*    {cmsContent.ShareholderStructure.referenceDate.content}*/}
                        {/*</Box>*/}
                    </>
                ),
            },
            {
                label: cmsContent.MajorShareholders.title.content,
                icon: EthicalIcon,
                content: <MajorShareholdersTable cmsContent={cmsContent.MajorShareholders} />,
            },
            {
                label: cmsContent.TaxReportingForms.title.content,
                icon: EthicalIcon,
                content: <TaxReportingForms contentTree={cmsContent.TaxReportingForms} />,
            },
        ],
        [cmsContent]
    );

    return (
        <>
            {isScreenLg || isScreenXl || isScreenXxl ? (
                <Box
                    sx={{
                        flexGrow: 1,
                        bgcolor: '#F8F8F8',
                        display: 'flex',
                        flexDirection: isScreenMd ? 'column' : 'row',
                        height: '100%',
                    }}
                >
                    <Tabs
                        orientation={isScreenMd ? 'horizontal' : 'vertical'}
                        value={value}
                        onChange={handleChange}
                        TabIndicatorProps={{ style: { display: 'none' } }}
                        sx={{
                            width: isScreenLg ? '20rem' : '22rem',
                            position: 'relative',

                            '& .MuiTab-root': {
                                color: '#E5673E',
                                alignItems: 'flex-start',
                                justifyContent: 'flex-start',
                                padding: '12px',
                                margin: '5px 0',
                                position: 'relative',
                                zIndex: 1,
                                '&:hover': {
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 1.1,
                                        left: 1.5,
                                        width: '20rem',
                                        height: '60px',
                                        backgroundColor: '#FCF0EC',
                                        zIndex: -1,
                                    },
                                },
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 1.1,
                                    left: 1.5,
                                    width: isScreenLg ? '19rem' : '20rem',
                                    height: '60px',
                                    backgroundColor: 'white',
                                    boxShadow:
                                        '1px 4px 8px rgba(0, 0, 0, 0.2),-1px 4px 8px rgba(0, 0, 0, 0.2)',
                                    zIndex: -1,
                                },
                            },
                            '& .Mui-selected': {
                                color: 'white!important',
                                backgroundColor: 'transparent',
                                boxShadow: 'none',
                                zIndex: 2,
                                path: {
                                    fill: 'white',
                                },
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 1.1,
                                    left: 1.5,
                                    width: isScreenLg ? '20rem' : '22rem',
                                    height: '60px',
                                    backgroundImage:
                                        'linear-gradient(90deg, #B75332 30%, #FAE1D8 100%)',
                                    zIndex: -1,
                                },
                            },
                            '& .Mui-selected::before': {
                                boxShadow: 'none',
                            },
                        }}
                    >
                        {panels.map((panel, index) => (
                            <Tab
                                key={index}
                                icon={
                                    <SvgIcon
                                        component={panel.icon}
                                        fill="none"
                                        sx={{ marginTop: '0.5rem' }}
                                    />
                                }
                                iconPosition="start"
                                label={
                                    <Typography
                                        sx={{
                                            marginTop: '0.5rem',
                                            marginLeft: '0.2rem',
                                            fontFamily: 'Poppins',
                                            lineHeight: '22px',
                                            fontSize: isScreenLg ? '16px' : '18px',
                                            fontWeight: 500,
                                            textTransform: 'none',
                                        }}
                                    >
                                        {panel.label}
                                    </Typography>
                                }
                                {...a11yProps(index)}
                            />
                        ))}
                    </Tabs>

                    {panels.map((panel, index) => (
                        <TabPanel key={index} value={value} index={index}>
                            <div>
                                <Accordion
                                    expanded={true}
                                    square
                                    sx={{
                                        backgroundColor: '#FAE1D8',
                                        width: isScreenLg
                                            ? '565px'
                                            : isScreenXl
                                              ? '760px'
                                              : '833px',
                                    }}
                                >
                                    <AccordionDetails>{panel.content}</AccordionDetails>
                                </Accordion>
                            </div>
                        </TabPanel>
                    ))}
                </Box>
            ) : (
                <Box
                    sx={{
                        flexGrow: 1,
                        bgcolor: '#F8F8F8',
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                    }}
                >
                    {panels.map((panel, index) => (
                        <Accordion
                            key={index}
                            expanded={expanded === index}
                            onChange={handleAccordionChange(index)}
                            sx={{
                                marginBottom: '1rem',
                                backgroundColor: '#FAE1D8',
                                width: isScreenLg
                                    ? '565px'
                                    : isScreenMd
                                      ? '650px'
                                      : isScreenSm
                                        ? '422px'
                                        : isScreenXs
                                          ? '295px'
                                          : '833px',
                                borderLeft: '2px solid #E0E0E0',
                                borderRight: '2px solid #E0E0E0',
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                    backgroundColor: expanded === index ? '#B75332' : 'white',
                                    color: expanded === index ? 'white' : '#E56731',
                                    '& .MuiSvgIcon-root': {
                                        color: expanded === index ? 'white' : '#E56731',
                                    },
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <CustomIcon
                                        component={panel.icon}
                                        expanded={expanded === index}
                                    />
                                    <Typography
                                        sx={{
                                            fontSize: isScreenXs ? '16px' : '20px',
                                            fontWeight: 500,
                                            marginLeft: '0.5rem',
                                        }}
                                    >
                                        {panel.label}
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>{panel.content}</AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            )}
        </>
    );
};

export default OverviewTab;
